// New Version Page Component
import { useEffect, useState, useMemo } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { Upload, FileUp, Loader2 } from 'lucide-react';
import { Button, Card, CardContent, Label, VersionCombobox, type VersionOption } from '@loader/ui';

import {
  TableSelector,
  VersionGuidelines,
} from '@/features/versions/components';
import { useSuggestTable } from '@/features/versions/hooks';
import { FileDropzone, FoldingCube } from '@/components/common';
import { useFormStore } from '@/store';
import { useVersionWithStorageLimit, useWorkspaceMembership } from '@/hooks';
import { toast } from '@/lib/toast';
import { useVersionUpload } from '@/features/workspace/hooks';
import { useTableVersionsQuery } from '@/features/tables/hooks/useTableQueries';

/** Shared styles for inline form field labels */
const LABEL_CLASS = 'text-muted-foreground w-24 shrink-0 text-sm';

export function NewVersionPage() {
  const navigate = useNavigate();
  const search = useSearch({ from: '/_authenticated/_withWorkspace/versions/new' });

  // Use Zustand form store for version state management
  const { versionForm, setVersionFile, setVersionTable, validateVersionForm, resetForm } =
    useFormStore();

  // Fetch current user's workspace role to check editor/owner access
  const { canEdit: canUpload, isLoading: isMembershipLoading } = useWorkspaceMembership();

  // Storage limit check with paywall dialog
  const { checkStorageBeforeUpload, StorageLimitDialog, isChecking } =
    useVersionWithStorageLimit();

  // Version upload hook - single source of truth for upload logic
  const { uploadFile, isUploading } = useVersionUpload();

  // Combined loading state
  const isVersioning = isChecking || isUploading;

  // Table suggestion hook - suggests table based on file field names
  const suggestTableMutation = useSuggestTable();

  // Excel sheet selection state (FileDropzone now handles extraction and UI)
  const [selectedSheet, setSelectedSheet] = useState<string | null>(null);

  // Base version ID to append to (null = replace mode)
  const [selectedBaseVersionId, setSelectedBaseVersionId] = useState<string | null>(null);

  // Fetch versions for the selected table (for append mode)
  const { data: allVersions = [] } = useTableVersionsQuery(versionForm.data.tableId, {
    enabled: !!versionForm.data.tableId,
  });

  // Filter to only completed versions and map to VersionOption format
  const completedVersions = useMemo((): VersionOption[] => {
    return allVersions
      .filter((v) => v.status === 'completed')
      .map((v) => ({
        id: v.id,
        file_name: v.file_name,
        version_number: v.version_number ?? undefined,
        status: v.status,
        system_tag: v.system_tag as 'current' | 'previous' | null,
        tags: v.tags ?? [],
        created_at: v.created_at,
      }));
  }, [allVersions]);

  // Reset form on mount
  useEffect(() => {
    resetForm('versionForm');
  }, [resetForm]);

  // Pre-select table if provided in search params (after form reset)
  useEffect(() => {
    if (search.tableId && typeof search.tableId === 'string') {
      setVersionTable(search.tableId);
    }
  }, [search.tableId, setVersionTable]);

  // Reset base version selection when table changes
  useEffect(() => {
    setSelectedBaseVersionId(null);
  }, [versionForm.data.tableId]);

  /**
   * Handle file selection
   * Suggests a matching table based on field names if no table is selected
   */
  const handleFileSelect = async (file: File) => {
    setVersionFile(file);

    // Only suggest table if no table is already selected
    if (!versionForm.data.tableId) {
      try {
        const result = await suggestTableMutation.mutateAsync(file);
        if (result.suggestion) {
          setVersionTable(result.suggestion.tableId);
          toast.success(
            'Table suggested',
            `Auto-selected "${result.suggestion.tableName}" based on matching fields`
          );
        }
      } catch {
        // Silently fail - suggestion is optional
      }
    }
  };

  const handleClearFile = () => {
    setVersionFile(null);
  };

  /**
   * Handle version creation
   * Checks storage limit, uploads the file, and navigates to progress page
   */
  const handleVersion = async () => {
    if (!validateVersionForm()) {
      return;
    }

    const file = versionForm.data.file;
    const tableId = versionForm.data.tableId;

    if (!file || !tableId) {
      toast.version.validationError('Please select a file and table');
      return;
    }

    // Check storage limit before uploading - shows paywall dialog if limit reached
    const canProceed = await checkStorageBeforeUpload(file.size);
    if (!canProceed) {
      // Reset file selection so user can try with a different/smaller file
      setVersionFile(null);
      setSelectedSheet(null);
      setSelectedBaseVersionId(null);
      return; // Dialog will be shown by the hook
    }

    // Use shared upload hook - handles validation, auth, and toasts
    const result = await uploadFile(file, tableId, {
      sheetName: selectedSheet ?? undefined,
      baseVersionId: selectedBaseVersionId ?? undefined,
    });

    if (result?.versionId) {
      // Navigate to progress page to show version processing
      navigate({
        to: '/versions/progress',
        search: { versionId: result.versionId },
      });
    }
  };

  // Check if version should be enabled (both file and table selected)
  const isVersionEnabled =
    versionForm.data.file !== null && versionForm.data.tableId.trim() !== '';

  // Loading state while checking membership
  if (isMembershipLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <FoldingCube text="Loading..." />
      </div>
    );
  }

  // Show access restricted for viewers
  if (!isMembershipLoading && !canUpload) {
    return (
      <div className="flex h-full flex-col items-center justify-center">
        <Upload className="text-muted-foreground mb-4 h-12 w-12" />
        <h2 className="text-xl font-semibold">Access Restricted</h2>
        <p className="text-muted-foreground mt-2 max-w-md text-center">
          Only workspace editors and owners can upload new versions. Contact your workspace owner for assistance.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold">New Version</h1>
          <p className="text-muted-foreground">
            Create a new file version for your table
          </p>
        </div>

        <div className="flex min-h-[70vh] items-center justify-center">
          <div className="w-full max-w-2xl">
            <Card>
              <CardContent className="space-y-4">
                <FileDropzone
                  onFileSelect={handleFileSelect}
                  selectedFile={versionForm.data.file}
                  onClearFile={handleClearFile}
                  onSheetChange={setSelectedSheet}
                  selectedSheet={selectedSheet}
                />

                {/* Table and append mode selection */}
                <div className="space-y-3">
                  <div className="flex items-center gap-4">
                    <Label className={LABEL_CLASS}>Table</Label>
                    <div className="flex-1">
                      <TableSelector
                        selectedTable={versionForm.data.tableId}
                        onTableChange={setVersionTable}
                      />
                    </div>
                  </div>

                  <div className="space-y-1">
                    <div className="flex items-center gap-4">
                      <Label className={LABEL_CLASS}>Append to</Label>
                      <div className="flex-1">
                        <VersionCombobox
                          versions={completedVersions}
                          value={selectedBaseVersionId ?? ''}
                          onChange={(value) => setSelectedBaseVersionId(value || null)}
                          placeholder="None (replace all data)"
                          allowNone
                          noneLabel="None (replace all data)"
                          showSystemTagShortcuts
                          showDate
                          showTags
                          className="w-full"
                          disabled={!versionForm.data.tableId || completedVersions.length === 0}
                        />
                      </div>
                    </div>
                    {selectedBaseVersionId && (
                      <p className="text-muted-foreground pl-28 text-xs">
                        New records will be added to the existing data. Unique field constraints will be validated.
                      </p>
                    )}
                  </div>
                </div>

                <Button
                  className="w-full"
                  onClick={handleVersion}
                  disabled={!isVersionEnabled || isVersioning}
                >
                  {isVersioning ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <FileUp className="mr-2 h-4 w-4" />
                      Upload
                    </>
                  )}
                </Button>

                <VersionGuidelines />
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Storage Limit Dialog - rendered by the hook */}
      <StorageLimitDialog />
    </>
  );
}
